import { accessToken } from "./auth.js";
import { normalizePirdyDeviceId } from "./pirdy-device-id.js";
import { withReadDeadline } from "./read-deadline.js";
import { loadCustomerWorkspace } from "./workspace.js";
import { playlistReferences } from "./customer-parity.js";
import { validatePirdyAccount } from "./account.js";
import { customerApiErrorMessage, customerOperationError, normalizeProblemCode } from "./error-presentation.js";

let baseUrl = "";
const recoverableReadStatuses = new Set([408, 429, 502, 503, 504]);
// A sleeping API plus SQL can take about a minute on the current low-cost tier.
const coldStartPreflights = new Set(["/v1/account", "/v1/me", "/v1/cloud-access"]);

const wait = milliseconds => new Promise(resolve => globalThis.setTimeout(resolve, milliseconds));

async function mapWithConcurrency(values, maximumConcurrency, operation) {
  const results = new Array(values.length);
  let nextIndex = 0;
  async function worker() {
    while (nextIndex < values.length) {
      const index = nextIndex++;
      results[index] = await operation(values[index], index);
    }
  }
  const workerCount = Math.min(values.length, maximumConcurrency);
  await Promise.all(Array.from({ length: workerCount }, () => worker()));
  return results;
}

export function configureApi(configuration) {
  baseUrl = configuration.apiBaseUrl.replace(/\/$/, "");
}

export function loadAssistantCapabilities(options = {}) {
  return request("/v1/assistant/capabilities", options);
}
export function uploadAssistantAttachment(payload, options = {}) {
  return withReadDeadline(signal => request("/v1/assistant/attachments", { ...options, signal, method: "POST", body: JSON.stringify(payload) }), { signal: options.signal, timeoutMs: 60000 });
}
export function createAssistantDraft(payload, options = {}) {
  return withReadDeadline(signal => request("/v1/assistant/drafts", { ...options, signal, method: "POST", body: JSON.stringify(payload) }), { signal: options.signal, timeoutMs: 60000 });
}
export function loadAssistantDraft(jobId, options = {}) {
  return request(`/v1/assistant/drafts/${encodeURIComponent(jobId)}`, options);
}
export function cancelAssistantDraft(jobId, options = {}) {
  return withReadDeadline(signal => request(`/v1/assistant/drafts/${encodeURIComponent(jobId)}`, { ...options, signal, method: "DELETE" }), { signal: options.signal, timeoutMs: 15000 });
}
export function downloadAssistantMedia(jobId, mediaId, options = {}) {
  return request(`/v1/assistant/drafts/${encodeURIComponent(jobId)}/media/${encodeURIComponent(mediaId)}`, options);
}

// Pin the complete prompt/import operation to one authentication proof. A tab
// switching accounts must never upload the old account's files into the new one.
export function createAssistantApiSession(isCurrent) {
  const originalBaseUrl = baseUrl;
  const knownJobs = new Set();
  let token;
  const send = async (path, options = {}) => {
    const check = () => { if (!isCurrent()) throw new DOMException("The Pirdy account changed.", "AbortError"); };
    check(); token ??= withReadDeadline(() => { check(); return accessToken(); });
    const bearerToken = await token; check();
    const result = await withReadDeadline(signal => { check(); return request(path, { ...options, signal, bearerToken }); }, { signal: options.signal, timeoutMs: 60000 });
    check(); return result;
  };
  const post = (path, payload, options) => send(path, { ...options, method: "POST", ...(payload ? { body: JSON.stringify(payload) } : {}) });
  const rememberJob = result => {
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(result?.jobId ?? "")) knownJobs.add(result.jobId.toLowerCase());
    return result;
  };
  // Closing an old workspace may cancel only a job already returned under its
  // captured proof. Never acquire a new account token or bypass the general
  // request scope guard; this fixed DELETE has no read/import capability.
  const cancelKnownJob = async (id, options = {}) => {
    const jobId = String(id ?? "").toLowerCase();
    if (!token || !knownJobs.has(jobId)) throw new DOMException("No original assistant job is available.", "AbortError");
    const bearerToken = await token;
    try {
      return await withReadDeadline(async signal => {
        const response = await fetch(`${originalBaseUrl}/v1/assistant/drafts/${encodeURIComponent(jobId)}`, {
          method: "DELETE", credentials: "omit", redirect: "error", signal,
          headers: { Accept: "application/json", Authorization: `Bearer ${bearerToken}` },
        });
        if (!response.ok) throw customerOperationError("pirdy_network_error");
        const result = await response.json();
        if (String(result?.jobId ?? "").toLowerCase() !== jobId || !["queued", "working", "ready", "failed", "cancelled", "expired"].includes(result.state)) throw customerOperationError("pirdy_network_error");
        return { contractVersion: 1, jobId, state: result.state };
      }, { signal: options.signal, timeoutMs: 15000 });
    } catch { throw customerOperationError("pirdy_network_error"); }
  };
  return {
    capabilities: options => send("/v1/assistant/capabilities", options),
    upload: (payload, options) => post("/v1/assistant/attachments", payload, options),
    create: (payload, options) => post("/v1/assistant/drafts", payload, options).then(rememberJob),
    load: (id, options) => send(`/v1/assistant/drafts/${encodeURIComponent(id)}`, options).then(rememberJob),
    cancel: cancelKnownJob,
    download: (id, mediaId, options) => send(`/v1/assistant/drafts/${encodeURIComponent(id)}/media/${encodeURIComponent(mediaId)}`, options),
    registerSource: payload => post("/v1/media/sources", payload),
    completeSource: id => post(`/v1/media/sources/${encodeURIComponent(id)}/complete`),
    registerArtwork: payload => post("/v1/media/generated-assets", payload),
    completeArtwork: id => post(`/v1/media/generated-assets/${encodeURIComponent(id)}/complete`),
  };
}

async function request(path, options = {}) {
  const method = String(options.method ?? "GET").toUpperCase();
  if (method !== "GET" && method !== "HEAD") return performRequest(path, options);
  try {
    return await withReadDeadline(signal => performRequest(path, { ...options, signal }), {
      signal: options.signal, timeoutMs: coldStartPreflights.has(path) ? 60000 : 15000,
    });
  } catch (error) {
    error.resource ??= path;
    throw error;
  }
}

async function performRequest(path, options = {}) {
  const { anonymous = false, bearerToken, ...fetchOptions } = options;
  const token = anonymous ? null : (bearerToken ?? await accessToken());
  if (fetchOptions.signal?.aborted) throw customerOperationError("pirdy_network_error");
  const method = String(fetchOptions.method ?? "GET").toUpperCase();
  const maximumAttempts = method === "GET" || method === "HEAD" ? 3 : 1;
  let response;
  for (let attempt = 1; attempt <= maximumAttempts; attempt++) {
    try {
      response = await fetch(`${baseUrl}${path}`, {
        ...fetchOptions,
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(fetchOptions.body ? { "Content-Type": "application/json" } : {}),
          ...fetchOptions.headers,
        },
      });
    } catch {
      if (fetchOptions.signal?.aborted) throw customerOperationError("pirdy_network_error");
      if (attempt < maximumAttempts) {
        await wait(600 * attempt);
        continue;
      }
      const error = customerOperationError("pirdy_network_error");
      error.resource = path;
      throw error;
    }
    if (!recoverableReadStatuses.has(response.status) || attempt === maximumAttempts) break;
    await response.body?.cancel();
    await wait(750 * attempt);
  }
  if (!response.ok) {
    let code = null;
    let body = null;
    try {
      body = await response.json();
      code = normalizeProblemCode(body?.code);
    } catch {
      // A non-JSON error must not mask the HTTP status.
    }
    const error = new Error(customerApiErrorMessage(response.status, code));
    error.status = response.status;
    error.code = code;
    error.resource = path;
    error.customerSafe = true;
    if (Array.isArray(body?.blockingPlaylists)) error.blockingPlaylists = body.blockingPlaylists;
    throw error;
  }
  if (response.status === 204) return null;
  const contentType = response.headers.get("content-type") ?? "";
  return contentType.includes("application/json") ? response.json() : response.blob();
}

export async function warmUpApi({ includeDependencies = false } = {}) {
  try {
    await request(includeDependencies ? "/health/ready" : "/health/live", { anonymous: true });
    return true;
  } catch {
    return false;
  }
}

export function loadPlanCatalog({ forCheckout = false } = {}) {
  if (forCheckout) return request("/v1/billing/checkout-plans");
  return request("/v1/billing/plans", { anonymous: true });
}

export async function loadWorkspace(options = {}) {
  // Pin all dataset reads to the same authenticated identity, including during account switching.
  const bearerToken = await withReadDeadline(() => accessToken());
  return loadCustomerWorkspace(path => request(path, { bearerToken }), options);
}

export function bootstrapCustomerAccount(payload) {
  return request("/v1/bootstrap", { method: "POST", body: JSON.stringify({ ...payload, deviceId: normalizePirdyDeviceId(payload?.deviceId) }) });
}

export async function loadPirdyAccount() {
  return validatePirdyAccount(await request("/v1/account"));
}

export async function registerPirdyAccount() {
  return validatePirdyAccount(await request("/v1/account", {
    method: "POST",
    body: JSON.stringify({ contractVersion: 1 }),
  }));
}

export function loadPlaylist(playlistId) {
  return request(`/v1/playlists/${encodeURIComponent(playlistId)}`);
}

export function loadWorkspaceSettingsSnapshot() {
  return request("/v1/workspace/snapshot?sinceVersion=0");
}

export function updateWorkspaceSettings(payload) {
  return request("/v1/workspace/mutations", { method: "POST", body: JSON.stringify(payload) });
}

export function loadBillingProfile() {
  return request("/v1/organization/billing-profile");
}

export function updateBillingProfile(payload) {
  return request("/v1/organization/billing-profile", { method: "PUT", body: JSON.stringify(payload) });
}

export function renameDevice(deviceId, nickname, version) {
  return request(`/v1/devices/${encodeURIComponent(deviceId)}/name`, { method: "PUT", headers: { "If-Match": `"${version}"` }, body: JSON.stringify({ nickname }) });
}

export function createSubgroup(name, deviceIds) {
  return request("/v1/subgroups", { method: "POST", body: JSON.stringify({ name, deviceIds }) });
}

export function updateSubgroup(groupId, version, name, deviceIds) {
  const quote = String.fromCharCode(34);
  return request(`/v1/subgroups/${encodeURIComponent(groupId)}`, { method: "PUT", headers: { "If-Match": `${quote}${version}${quote}` }, body: JSON.stringify({ name, deviceIds }) });
}

export function deleteSubgroup(groupId) {
  return request(`/v1/subgroups/${encodeURIComponent(groupId)}`, { method: "DELETE" });
}

export function createParentGroup(name, groupIds) {
  return request("/v1/parent-groups", { method: "POST", body: JSON.stringify({ name, groupIds }) });
}

export function updateParentGroup(parentGroupId, version, name, groupIds) {
  const quote = String.fromCharCode(34);
  return request(`/v1/parent-groups/${encodeURIComponent(parentGroupId)}`, { method: "PUT", headers: { "If-Match": `${quote}${version}${quote}` }, body: JSON.stringify({ name, groupIds }) });
}

export function deleteParentGroup(parentGroupId) {
  return request(`/v1/parent-groups/${encodeURIComponent(parentGroupId)}`, { method: "DELETE" });
}

export function registerMedia(payload) {
  return request("/v1/media/generated-assets", { method: "POST", body: JSON.stringify(payload) });
}

export function registerContentSource(payload) {
  return request("/v1/media/sources", { method: "POST", body: JSON.stringify(payload) });
}

export function completeContentSource(sourceId) {
  return request(`/v1/media/sources/${encodeURIComponent(sourceId)}/complete`, { method: "POST" });
}

export function createContentSourceDownloads() {
  return request("/v1/media/source-downloads");
}

export function completeMedia(assetId) {
  return request(`/v1/media/generated-assets/${encodeURIComponent(assetId)}/complete`, { method: "POST" });
}

export function createMediaDownloads() {
  return request("/v1/media/downloads");
}

export function deleteMedia(assetId) {
  return request(`/v1/media/generated-assets/${encodeURIComponent(assetId)}`, { method: "DELETE" });
}

export async function loadSavedPlaylistReferences(assetId) {
  const bearerToken = await accessToken();
  try {
    const summaries = await request("/v1/playlists", { bearerToken });
    const values = Array.isArray(summaries) ? summaries
      : Array.isArray(summaries?.items) ? summaries.items
      : Array.isArray(summaries?.value) ? summaries.value
      : null;
    if (!values) throw new Error("invalid playlist list");
    const rawIds = values.map(item => String(item?.id ?? item?.playlistId ?? ""));
    if (rawIds.some(id => !id)) throw new Error("invalid playlist summary");
    const ids = [...new Set(rawIds)];
    const details = await mapWithConcurrency(ids, 4,
      id => request(`/v1/playlists/${encodeURIComponent(id)}`, { bearerToken }));
    if (details.some((item, index) =>
      String(item?.id ?? item?.playlistId ?? "").toLowerCase() !== ids[index].toLowerCase() ||
      (!Array.isArray(item?.items) && !Array.isArray(item?.items?.items) && !Array.isArray(item?.items?.value)))) {
      throw new Error("invalid playlist detail");
    }
    return playlistReferences(assetId, details);
  } catch (cause) {
    if (cause?.status === 401 || cause?.status === 403) throw cause;
    const error = new Error("Saved playlists could not be checked. Content was not deleted.");
    error.code = "pirdy_playlist_reference_check_failed";
    error.customerSafe = true;
    error.cause = cause;
    throw error;
  }
}

export async function createPlaylist(payload) {
  if (payload.requestId) {
    const capabilities = await request("/v1/authoring-capabilities");
    if (capabilities?.playlistCreateContractVersion !== 1) throw new Error("Playlist saving is temporarily unavailable. Nothing was created.");
  }
  return request("/v1/playlists", { method: "POST", body: JSON.stringify(payload) });
}

export function updatePlaylist(playlistId, version, payload) {
  const quote = String.fromCharCode(34);
  return request(`/v1/playlists/${encodeURIComponent(playlistId)}`, {
    method: "PUT",
    headers: { "If-Match": `${quote}${version}${quote}` },
    body: JSON.stringify(payload),
  });
}

export function deletePlaylist(playlistId) {
  return request(`/v1/playlists/${encodeURIComponent(playlistId)}`, { method: "DELETE" });
}

export function registerBranding(payload) {
  return request("/v1/organization/branding", { method: "POST", body: JSON.stringify(payload) });
}

export function completeBranding(version) {
  return request(`/v1/organization/branding/${encodeURIComponent(version)}/complete`, { method: "POST" });
}

export function removeBranding() {
  return request("/v1/organization/branding", { method: "DELETE" });
}

export function validatePublication(payload) {
  return request("/v1/publications/validate", { method: "POST", body: JSON.stringify(payload) });
}

export function publishContent(payload) {
  return request("/v1/publications", { method: "POST", body: JSON.stringify(payload) });
}

export function setDeviceDisplay(deviceId, displayOn, requestId = crypto.randomUUID()) {
  return request(`/v1/devices/${encodeURIComponent(deviceId)}/display-power`, {
    method: "PUT",
    body: JSON.stringify({ displayOn, requestId }),
  });
}

export function setGroupDisplay(groupId, displayOn, requestId = crypto.randomUUID()) {
  return request(`/v1/subgroups/${encodeURIComponent(groupId)}/display-power`, {
    method: "PUT",
    body: JSON.stringify({ displayOn, requestId }),
  });
}

export function setParentGroupDisplay(parentGroupId, displayOn, requestId = crypto.randomUUID()) {
  return request(`/v1/parent-groups/${encodeURIComponent(parentGroupId)}/display-power`, {
    method: "PUT",
    body: JSON.stringify({ displayOn, requestId }),
  });
}

export function loadSupportTickets() {
  return request("/v1/support/tickets");
}

export function createSupportTicket(payload) {
  return request("/v1/support/tickets", { method: "POST", body: JSON.stringify(payload) });
}

export function loadBilling() {
  return request("/v1/billing");
}

export function loadCloudEntitlement() {
  return request("/v1/billing/entitlement");
}

export function loadCloudAccess() {
  return request('/v1/cloud-access');
}

export function loadClaimedDevices() {
  return request('/v1/devices');
}

export function loadAccountProfile() {
  return request('/v1/me');
}

export function loadOrganizationUsers() {
  return request("/v1/organization/users");
}

export function updateOrganizationUser(userId, payload) {
  return request(`/v1/organization/users/${encodeURIComponent(userId)}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export function loadOrganizationInvitations() {
  return request("/v1/organization/invitations");
}

export function inviteOrganizationUser(payload) {
  return request("/v1/organization/invitations", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function revokeOrganizationInvitation(invitationId) {
  return request(`/v1/organization/invitations/${encodeURIComponent(invitationId)}/revoke`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function createPlanCheckout(planCode, confirmedPurchasedPirdy, expectedCatalogPublicationSequence) {
  return request("/v1/billing/plan-checkout", {
    method: "POST",
    body: JSON.stringify({ requestId: crypto.randomUUID(), planCode, confirmedPurchasedPirdy, expectedCatalogPublicationSequence }),
  });
}

export function changePlan(planCode, atPeriodEnd, confirmedPurchasedPirdy, expectedCatalogPublicationSequence) {
  return request("/v1/billing/plan-change", {
    method: "POST",
    body: JSON.stringify({ requestId: crypto.randomUUID(), planCode, atPeriodEnd, confirmedPurchasedPirdy, expectedCatalogPublicationSequence }),
  });
}

export function createSingleChangeCheckout(confirmedPurchasedPirdy, expectedCatalogPublicationSequence) {
  return request("/v1/billing/change-pass-checkout", {
    method: "POST",
    body: JSON.stringify({ requestId: crypto.randomUUID(), confirmedPurchasedPirdy, expectedCatalogPublicationSequence }),
  });
}

export function loadHardwareOffers() {
  return request("/v1/hardware/offers", { anonymous: true });
}

export function startClaimSession(deviceId, requestId) {
  return request("/v1/devices/claim-session", {method: "POST", body: JSON.stringify({deviceId: normalizePirdyDeviceId(deviceId), requestId})});
}
export function claimPirdy(deviceId, pin, sessionId) {
  deviceId = normalizePirdyDeviceId(deviceId);
  return request("/v1/devices/claim", {
    method: "POST",
    body: JSON.stringify({ deviceId, pin, sessionId }),
  });
}

export function startCloudTrial(sponsorDeviceId) {
  return request("/v1/cloud-trial/start", {
    method: "POST",
    body: JSON.stringify({ requestId: crypto.randomUUID(), sponsorDeviceId }),
  });
}

export function reportPirdyStolen(deviceId, reason) {
  return request(`/v1/devices/${encodeURIComponent(deviceId)}/protection/report`, {
    method: "POST",
    body: JSON.stringify({ requestId: crypto.randomUUID(), confirmation: "REPORT STOLEN", reason }),
  });
}

export function reEnableProtectedPirdy(deviceId) {
  return request(`/v1/devices/${encodeURIComponent(deviceId)}/protection/clear`, {
    method: "POST",
    body: JSON.stringify({ requestId: crypto.randomUUID(), confirmation: "RE-ENABLE PIRDY" }),
  });
}

export function createPaymentSession(invoiceId) {
  return request(`/v1/billing/invoices/${encodeURIComponent(invoiceId)}/payment-session`, {
    method: "POST",
    body: JSON.stringify({ requestId: crypto.randomUUID() }),
  });
}

export async function downloadInvoice(invoiceId, fallbackFileName) {
  const blob = await request(`/v1/billing/invoices/${encodeURIComponent(invoiceId)}/document`);
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fallbackFileName || `${invoiceId}.pdf`;
  link.click();
  URL.revokeObjectURL(url);
}

export function getDeviceSettings(deviceId) {
  return request(`/v1/devices/${encodeURIComponent(deviceId)}/settings`);
}
export function saveDeviceSettings(deviceId, change) {
  return request(`/v1/devices/${encodeURIComponent(deviceId)}/settings`, { method: "PUT", body: JSON.stringify(change) });
}
